import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { CharacterCount, Label } from '@/components/ui/label'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import { T, useTranslate } from '@tolgee/react'
import React from 'react'
import { Field, Form } from 'react-final-form'

type DialogInputFormData = {
  inputValue: string
}

type DialogInputFormProps = Readonly<{
  openState?: {
    open: boolean
    onOpenChange: SetState<boolean>
  }
  onSubmit: ({ inputValue }: DialogInputFormData) => any
  title: string
  descriptionHidden: string
  isLoading?: boolean
  initialValue?: string | null
  maxInputLength?: number
  placeholder?: string
  triggerNode?: React.ReactNode
  submitButtonText?: string
}>

export function DialogInputForm({
  openState,
  onSubmit,
  title,
  descriptionHidden,
  isLoading,
  initialValue,
  maxInputLength = 200,
  triggerNode,
  placeholder,
  submitButtonText,
}: DialogInputFormProps) {
  const { t } = useTranslate()
  const [localOpen, setLocalOpen] = React.useState(false)
  const [open, setOpen] = [openState?.open ?? localOpen, openState?.onOpenChange ?? setLocalOpen]
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {triggerNode && <DialogTrigger asChild>{triggerNode}</DialogTrigger>}
      <DialogPortal>
        <DialogContent
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Form<DialogInputFormData>
            initialValues={{ inputValue: initialValue ?? '' }}
            validate={({ inputValue }) => {
              if (!inputValue?.trim() || inputValue.length > maxInputLength) {
                return { inputValue: true }
              }
            }}
            onSubmit={async ({ inputValue }) => {
              await onSubmit({ inputValue })
              setOpen(false)
            }}
          >
            {({ handleSubmit, dirty, invalid }) => (
              <form className='flex flex-col' onSubmit={handleSubmit}>
                <DialogTitle className='mb-4 text-2xl'>
                  <Label htmlFor='inputValue'>{title}</Label>
                </DialogTitle>
                <VisuallyHidden asChild>
                  <DialogDescription>{descriptionHidden}</DialogDescription>
                </VisuallyHidden>
                <Field name='inputValue'>
                  {({ input, meta }) => (
                    <div>
                      <Input
                        placeholder={placeholder}
                        error={meta.invalid && !meta.pristine}
                        disabled={isLoading}
                        {...input}
                      />
                      <CharacterCount max={maxInputLength} length={input.value.length} />
                    </div>
                  )}
                </Field>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button
                      disabled={isLoading}
                      className='uppercase'
                      variant='mango-outline'
                      title={t('shared.cancelDialog.a11y')}
                    >
                      <T keyName='shared.cancelDialog.title' />
                    </Button>
                  </DialogClose>
                  <Button
                    type='submit'
                    className='uppercase'
                    loading={isLoading}
                    title='Save changes'
                    disabled={!dirty || invalid}
                  >
                    {submitButtonText ?? 'Send'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </Form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
